/* eslint-disable no-else-return */
/* eslint-disable arrow-body-style */
import { useMemo, useState } from 'react';
import { getLocaleText } from '@intelligence/components/GenreSelect/utils';

import type {
  IShortcut,
  IGenreCheckboxOption,
  IGenreCheckboxOptionWithChildren,
} from '@intelligence/components/GenreSelect/types';

type IGenreFilter = (genre: IGenreCheckboxOption, index?: number) => boolean;

type IGenreFilterBuilder = (locale: string, shortcut: string, keywords: string) => IGenreFilter;

const formatKeywords = (
  locale: string,
  props: {
    shortcuts: IShortcut[];
    keywords: string;
  },
): {
  keywords: string;
  shortcut: string;
} => {
  const { shortcuts, keywords } = props || {};
  if (typeof keywords !== 'string') return { keywords: '', shortcut: '' };
  const keywordsTmp = keywords.toLowerCase();
  const keywordsShortcut = shortcuts?.find((shortcut) => {
    const shortcutText = getLocaleText(locale, shortcut);
    return shortcutText?.toLowerCase() === keywordsTmp;
  });
  return {
    keywords: keywordsTmp,
    shortcut: keywordsShortcut?.value || '',
  };
};

const buildFilterGenre: IGenreFilterBuilder = (locale, shortcut, keywords) => (genre) => {
  // const text = getLocaleText(locale, genre);
  const text = genre?.text || '';
  const textCN = genre?.textCN || '';
  return (
    genre?.value === shortcut ||
    text.toLowerCase().includes(keywords) ||
    textCN.toLowerCase().includes(keywords)
  );
};

/**
 * 递归过滤品类树（支持级联搜索）
 * @param genres 品类数组
 * @param filterFn 过滤函数
 * @param depth 当前递归深度
 * @param maxDepth 最大递归深度限制
 * @returns 过滤后的品类数组
 */
const filterGenresRecursive = (
  genres: IGenreCheckboxOptionWithChildren[],
  filterFn: IGenreFilter,
  depth = 0,
  maxDepth = 10,
): IGenreCheckboxOptionWithChildren[] => {
  if (!Array.isArray(genres) || genres.length === 0) {
    return [];
  }

  // 递归深度限制，防止栈溢出
  if (depth > maxDepth) {
    console.error('[filterGenresRecursive] 递归深度超限,可能存在循环引用, depth:', depth);
    return [];
  }

  return genres
    .map((genre) => {
      if (!genre) {
        return null;
      }

      // 当前节点是否匹配
      const matchesSelf = filterFn(genre);
      // 如果当前节点匹配，保留所有子级
      if (matchesSelf) {
        return { ...genre, children: genre.children || [] };
      }

      const children = genre.children as IGenreCheckboxOptionWithChildren[] | undefined;
      const hasChildren = Array.isArray(children) && children.length > 0;

      // 如果当前节点不匹配，递归过滤子级
      if (hasChildren) {
        const filteredChildren = filterGenresRecursive(children, filterFn, depth + 1, maxDepth);
        if (filteredChildren.length > 0) {
          return { ...genre, children: filteredChildren };
        }
      }

      // 当前节点不匹配且没有匹配的子级，返回 null
      return null;
    })
    .filter((genre): genre is IGenreCheckboxOptionWithChildren => genre !== null);
};

export default function useSearch(
  locale: string,
  props: {
    shortcuts: IShortcut[];
    genres: IGenreCheckboxOptionWithChildren[];
  },
) {
  const { shortcuts, genres: genresProps } = props || {};

  const [keywords, setKeywords] = useState('');

  const isSearching = useMemo<boolean>(() => keywords?.length > 0, [keywords]);

  const genres = useMemo<IGenreCheckboxOptionWithChildren[]>(() => {
    if (!Array.isArray(genresProps)) return [];
    const { shortcut, keywords: keywordsTmp } = formatKeywords(locale, {
      shortcuts,
      keywords,
    });

    if (!keywordsTmp) return genresProps;
    const filterGenre = buildFilterGenre(locale, shortcut, keywordsTmp);

    // 使用递归过滤函数支持级联搜索
    return filterGenresRecursive(genresProps, filterGenre);
  }, [locale, keywords, genresProps]);

  const handleSearch = (payload: string) => {
    setKeywords(payload?.trim() || '');
  };

  return {
    isSearching,
    keywords,
    setKeywords,
    genres,
    onSearch: handleSearch,
  };
}
